/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cx from 'classnames';

import { getStateAbbreviation, kilometersToMiles } from '../utils/location';

import styles from './CardLocation.module.scss';

interface CardLocationProps {
    parent?: string;
    organization?: string;
    distance?: string;
    city?: string;
    state?: string;
    className?: string;
}

interface LocationText {
    /** Parent area name, falling back to the managing organization. */
    parentText: string;
    /** e.g. "12 miles away near Moab, UT", "<1 mile away", or "Near Moab, UT". */
    distanceText: string;
}

/** Whole-mile formatter with correct pluralization ("1 mile", "12 miles"). */
const milesFormatter = new Intl.NumberFormat('en-US', {
    style: 'unit',
    unit: 'mile',
    unitDisplay: 'long',
    maximumFractionDigits: 0,
});

/**
 * e.g. "12 miles away", or "<1 mile away" when the distance rounds to 0 miles.
 * Empty when there's no distance, or it's exactly 0: that's the searched place
 * itself, which isn't any distance away.
 * @param miles the distance in miles
 */
function formatMilesAway(miles: number | null): string {
    if (miles === null || miles === 0) {
        return '';
    }
    if (Math.round(miles) === 0) {
        return '<1 mile away';
    }
    return `${milesFormatter.format(miles)} away`;
}

/**
 * Builds the display strings shared by the full and compact location variants.
 */
function getLocationText({
    parent,
    organization,
    distance,
    city,
    state,
}: Omit<CardLocationProps, 'className'>): LocationText {
    const miles = kilometersToMiles(distance);
    const nearCityState =
        city && state ? `${city}, ${getStateAbbreviation(state) ?? state}` : '';
    const milesAway = formatMilesAway(miles);

    let distanceText = '';
    if (milesAway && nearCityState) {
        distanceText = `${milesAway} near ${nearCityState}`;
    } else if (milesAway) {
        distanceText = milesAway;
    } else if (nearCityState) {
        distanceText = `Near ${nearCityState}`;
    }

    return { parentText: parent || organization || '', distanceText };
}

export function CardLocation({ className, ...props }: CardLocationProps) {
    const { parentText, distanceText } = getLocationText(props);

    return (
        <div className={cx(styles.location, className)}>
            {parentText && <div className={styles.parent}>{parentText}</div>}
            {distanceText && <div className={styles.distanceAndNear}>{distanceText}</div>}
        </div>
    );
}

export function CompactCardLocation({ className, ...props }: CardLocationProps) {
    const { parentText, distanceText } = getLocationText(props);
    const text = [parentText, distanceText].filter(Boolean).join(' • ');

    return (
        <div className={cx(styles.location, className)}>
            <div className={styles.compactLocation}>{text}</div>
        </div>
    );
}
